import { useCallback } from 'react';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';
import {
  Token, TokenStatus, TokensSummary, CreateWalkInTokenRequest, CompleteTokenRequest,
} from '../api/tokenTypes';

/**
 * Token operations (provider side). Queue-scoped list/walk-in/slot use the
 * /queues base; single-token actions use the /tokens base. See plan §2.4.
 */
const QUEUES = API_ENDPOINTS.GET_QUEUES;
const TOKENS = API_ENDPOINTS.TOKENS;

export interface TokensResult {
  tokens: Token[];
  summary: TokensSummary | null;
}

export const useTokens = () => {
  const getTokens = useCallback(
    async (queueId: string, date: string, opts?: { status?: TokenStatus; page?: number; limit?: number }): Promise<TokensResult> => {
      const qs = new URLSearchParams({ date });
      if (opts?.status) qs.set('status', opts.status);
      qs.set('page', String(opts?.page ?? 1));
      // backend caps limit at 100 — sending more fails validation (400)
      qs.set('limit', String(Math.min(opts?.limit ?? 100, 100)));
      const res = await axiosInstance.get(`${QUEUES}/${queueId}/tokens?${qs.toString()}`);
      return { tokens: res.data?.data ?? [], summary: res.data?.summary ?? null };
    },
    []
  );

  const getToken = useCallback(async (tokenId: string): Promise<Token> => {
    const res = await axiosInstance.get(`${TOKENS}/${tokenId}`);
    return res.data.data;
  }, []);

  const createWalkIn = useCallback(async (queueId: string, payload: CreateWalkInTokenRequest): Promise<Token> => {
    const res = await axiosInstance.post(`${QUEUES}/${queueId}/tokens`, payload);
    return res.data.data;
  }, []);

  const updateStatus = useCallback(async (tokenId: string, status: TokenStatus): Promise<Token> => {
    const res = await axiosInstance.patch(`${TOKENS}/${tokenId}/status`, { status });
    return res.data.data;
  }, []);

  const assignProvider = useCallback(async (tokenId: string, providerId: string): Promise<Token> => {
    const res = await axiosInstance.patch(`${TOKENS}/${tokenId}/provider`, { provider_id: providerId });
    return res.data.data;
  }, []);

  const completeToken = useCallback(async (tokenId: string, payload: CompleteTokenRequest): Promise<Token> => {
    const res = await axiosInstance.patch(`${TOKENS}/${tokenId}/complete`, payload);
    return res.data.data;
  }, []);

  return { getTokens, getToken, createWalkIn, updateStatus, assignProvider, completeToken };
};
